import { fassungsschild } from '@nac2s/shared'
import { useState } from 'react'
import { NavLink, Outlet } from 'react-router'
import { Wortmarke } from '@/teile/Wortmarke'
import { cx } from '@/utils/cx'
import { abmelden } from '../api/verwaltung.js'
import { Anmelden } from '../pages/Anmelden.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Rahmen der Verwaltung — dieselbe Karte, dieselbe Kopfleiste wie die
 * Kundenverwaltung; nur der Beisatz sagt, wo man ist.
 */
const NAVIGATION = [
  { pfad: '/', beschriftung: 'Partner', genau: true },
  { pfad: '/editionen', beschriftung: 'Editionen', genau: false },
  { pfad: '/auftraege', beschriftung: 'Aufträge', genau: false },
  { pfad: '/abrechnung', beschriftung: 'Abrechnung', genau: false },
  { pfad: '/proxy', beschriftung: 'Proxy', genau: false },
] as const

export function AppShell() {
  const { zustand, neuLaden } = useSitzung()
  const [meldetAb, setMeldetAb] = useState(false)

  if (zustand.status === 'unbekannt') {
    return (
      <p role="status" className="p-8 text-sm text-tertiary">
        Wird geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler') {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="rounded-xl bg-error-primary p-4 text-sm text-error-primary">
          <p className="font-medium">Der Dienst ist nicht erreichbar</p>
          <p className="mt-1">{zustand.meldung}</p>
        </div>
      </div>
    )
  }

  if (zustand.status === 'abgemeldet') return <Anmelden />

  async function abmeldenUndNeuLaden() {
    setMeldetAb(true)
    try {
      await abmelden()
    } finally {
      setMeldetAb(false)
      await neuLaden()
    }
  }

  return (
    <div className="min-h-screen bg-secondary px-4 py-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-primary shadow-lg">
        <header className="flex flex-wrap items-center gap-3.5 border-b border-secondary px-5 py-[13px]">
          <Wortmarke beisatz="Verwaltung" />

          <nav aria-label="Hauptbereiche" className="ml-1 flex gap-0.5">
            {NAVIGATION.map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.genau}
                className={({ isActive }) =>
                  cx(
                    'rounded-full px-3.5 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-akzent-flaeche font-semibold text-akzent-tief'
                      : 'text-tertiary hover:bg-secondary hover:text-primary',
                  )
                }
              >
                {eintrag.beschriftung}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span
              aria-hidden="true"
              className="grid size-8 place-items-center rounded-full bg-linear-to-br from-marke-blau to-marke-violett text-xs font-semibold text-white"
            >
              {initialen(zustand.sitzung.anzeigename)}
            </span>
            <button
              type="button"
              onClick={abmeldenUndNeuLaden}
              disabled={meldetAb}
              className="cursor-pointer rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-primary transition-colors hover:bg-secondary disabled:opacity-50"
            >
              Abmelden
            </button>
          </div>
        </header>

        <Outlet />

        <Fassungszeile />
      </div>
    </div>
  )
}

/**
 * Die ausgelieferte Fassung, klein und immer sichtbar.
 *
 * `DOCUMENTATION.md` §1: Neben der Nummer steht der Git-Kurz-Hash — er
 * unterscheidet zwei Stände derselben Nummer. Der Hash kommt beim Bauen aus
 * `VITE_NADIA_BUILD`; fehlt er, bleibt es bei der Nummer.
 */
function Fassungszeile() {
  return (
    <footer className="border-t border-secondary px-6 py-3 text-right">
      <span className="text-[11.5px] text-quaternary tabular-nums" title="Ausgelieferte Fassung">
        VeeKy {fassungsschild(import.meta.env.VITE_NADIA_BUILD)}
      </span>
    </footer>
  )
}

export function initialen(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0])
    .join('')
    .toUpperCase()
}

/** Derselbe Seitenkopf wie in den anderen Oberflaechen. */
export function Seitenkopf({
  beisatz,
  titel,
  satz,
  rechts,
}: {
  beisatz: string
  titel: string
  satz: string
  rechts?: React.ReactNode
}) {
  return (
    <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
      <div
        aria-hidden="true"
        className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
      />
      <div className="relative flex flex-wrap items-end gap-4">
        <div>
          <p className="text-[13px] text-quaternary">{beisatz}</p>
          <h1 className="mt-4 font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
            {titel}
          </h1>
          <p className="mt-1 text-sm text-tertiary">{satz}</p>
        </div>
        {rechts ? <div className="ml-auto flex flex-wrap gap-2">{rechts}</div> : null}
      </div>
    </div>
  )
}
